import { useEffect, useRef, useMemo } from 'react';
import { userService } from '../_services';
import { fireSessionEndedUI, isAuthPathForSessionEndedSkip } from '../sessionEndedEvents';

// Written by extension logic; absolute ms timestamp. Cleared by logout (localStorage.clear).
const EXPIRY_OVERRIDE_KEY = '_session_expiry_override';

// How often the session-expiry check ticks (unchanged from the original 30s).
const CHECK_INTERVAL_MS = 30 * 1000;

/**
 * Per-environment tuning.
 *
 * Localhost  : 10 min session │ +10 min extension │ warn at 2 min remaining │ poll every 1 min
 * Dev/test   : 3 day session  │ +24 h extension   │ warn at 30 min remaining │ poll every 30 min
 * Production : 7 day session  │ +24 h extension   │ warn at 30 min remaining │ poll every 30 min
 */
function getEnvConfig() {
    const hostname = window.location.hostname.toLowerCase();
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isTestDomain = ['tempfilter.23byt.es', 'dev.glichealth.com'].includes(hostname);

    if (isLocalhost) {
        return {
            SESSION_MS:   10 * 60 * 1000,
            EXTENSION_MS: 10 * 60 * 1000,
            WARN_MS:       2 * 60 * 1000,
            POLL_MS:       1 * 60 * 1000,
        };
    }
    if (isTestDomain) {
        return {
            SESSION_MS:    30 * 60 * 60 * 1000,        // 30 hours
            EXTENSION_MS:      24 * 60 * 60 * 1000,
            WARN_MS:           30 * 60 * 1000,
            POLL_MS:           30 * 60 * 1000,
        };
    }
    return {
        SESSION_MS:    7 * 24 * 60 * 60 * 1000,
        EXTENSION_MS:      24 * 60 * 60 * 1000,
        WARN_MS:           30 * 60 * 1000,
        POLL_MS:           30 * 60 * 1000,
    };
}

/**
 * Replaces the simple session-expiry useEffect that was in App.jsx.
 *
 * Two independent timers run while the user is logged in:
 *
 *  POLL  (every POLL_MS)  — calls CheckSessionActivity and caches the last
 *                           { hasActivity } result in a ref.
 *
 *  CHECK (every 30 s)     — computes remaining time and, once per warning
 *                           window, either extends the session or lets it
 *                           expire based on the cached activity result.
 *
 * Extension is stored as an absolute timestamp in localStorage under
 * `_session_expiry_override`.  logout() already calls localStorage.clear()
 * so no extra cleanup is needed there.
 */
export function useSessionActivityGuard() {
    const { SESSION_MS, EXTENSION_MS, WARN_MS, POLL_MS } = useMemo(getEnvConfig, []);

    // Last result from CheckSessionActivity. null = not yet polled.
    const lastActivityRef = useRef(null);

    // True while we are inside a warning window to prevent acting more than once.
    // Resets to false when remaining time climbs back above WARN_MS (after an extension).
    const actedInWindowRef = useRef(false);

    // Prevents firing the account-status logout more than once per session.
    const accountRevokedFiredRef = useRef(false);

    // ── Activity polling ──────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;

        async function poll() {
            if (cancelled) return;
            try {
                const raw = localStorage.getItem('user');
                if (!raw || raw === '{}' || raw === 'null') return;
                const user = JSON.parse(raw);
                if (!user?.id) return;

                const result = await userService.checkSessionActivity(user.id);
                if (!cancelled && result !== null) {
                    lastActivityRef.current = {
                        hasActivity: result.hasActivity === true,
                        accountStatus: result.accountStatus || 'active',
                        statusMessage: result.statusMessage || '',
                        wholesalerName: result.wholesalerName || '',
                        ts: Date.now(),
                    };

                    // If the account is no longer active, logout and show the
                    // session-ended modal immediately (don't wait for expiry check).
                    const status = result.accountStatus;
                    if (status && status !== 'active' && !accountRevokedFiredRef.current) {
                        accountRevokedFiredRef.current = true;
                        userService.logout({ sessionEndedHold: true });
                        fireSessionEndedUI({
                            accountStatus: status,
                            statusMessage: result.statusMessage || '',
                            wholesalerName: result.wholesalerName || '',
                        });
                    }
                }
            } catch {
                // Network error — keep the previous cached value
            }
        }

        poll(); // fire immediately on mount
        const id = setInterval(poll, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [POLL_MS]);

    // ── Session expiry + extension check ─────────────────────────────────────
    useEffect(() => {
        function check() {
            const raw = localStorage.getItem('user');
            const isLoggedIn = raw && raw !== '' && raw !== 'null' && raw !== '{}';
            if (!isLoggedIn) {
                actedInWindowRef.current = false;
                return;
            }

            const loginTime = parseInt(localStorage.getItem('_login_time') || '0', 10);

            // No login timestamp means the session predates this feature — force logout once.
            if (!loginTime) {
                userService.logout({ sessionEndedHold: true });
                fireSessionEndedUI();
                return;
            }

            const overrideUntil = parseInt(localStorage.getItem(EXPIRY_OVERRIDE_KEY) || '0', 10);
            const expiresAt     = overrideUntil > 0 ? overrideUntil : loginTime + SESSION_MS;
            const remaining     = expiresAt - Date.now();

            // ── Expired ──
            if (remaining <= 0) {
                localStorage.removeItem(EXPIRY_OVERRIDE_KEY);
                actedInWindowRef.current = false;
                userService.logout({ sessionEndedHold: true });
                fireSessionEndedUI();
                return;
            }

            // ── Outside warning window: reset the acted flag so the next window works ──
            if (remaining > WARN_MS) {
                actedInWindowRef.current = false;
                return;
            }

            // ── Inside warning window: act once ──
            if (actedInWindowRef.current) return;
            actedInWindowRef.current = true;

            // Skip extension logic while the forced password-reset popup is active —
            // the 30-day hold already covers the session; touching the override here
            // could only shorten it.
            if (localStorage.getItem('_pwd_reset_active') === '1') return;

            const last = lastActivityRef.current;
            // Don't extend if account access was already revoked/deactivated.
            if (last?.accountStatus && last.accountStatus !== 'active') return;
            if (last?.hasActivity === true) {
                // User has been active → extend the session
                localStorage.setItem(EXPIRY_OVERRIDE_KEY, (Date.now() + EXTENSION_MS).toString());
            }
            // hasActivity === false (or null / never polled) → do nothing; let the
            // expiry check above handle logout when remaining hits 0.
        }

        check();
        const id = setInterval(check, CHECK_INTERVAL_MS);
        return () => clearInterval(id);
    }, [SESSION_MS, EXTENSION_MS, WARN_MS]);

    // ── bfcache restore guard ─────────────────────────────────────────────────
    // When a user closes/backgrounds a tab and reopens it after the session has
    // expired, the browser thaws the frozen page from bfcache before JavaScript
    // resumes.  The 30s check above won't fire for up to 30 seconds, and
    // PrivateRoute may redirect to /login before the modal appears.
    // `pageshow` with `event.persisted = true` fires the instant the frozen page
    // is restored — we use it to detect this gap and show the popup immediately.
    useEffect(() => {
        function handlePageShow(e) {
            // Fresh page loads are handled by the normal boot path — skip them.
            if (!e.persisted) return;

            // Don't interfere while the user is already on an auth route.
            if (isAuthPathForSessionEndedSkip(window.location.pathname)) return;

            const raw        = localStorage.getItem('user');
            const isLoggedIn = raw && raw !== '{}' && raw !== 'null' && raw !== '';
            const loginTime  = parseInt(localStorage.getItem('_login_time') || '0', 10);
            const override   = parseInt(localStorage.getItem(EXPIRY_OVERRIDE_KEY) || '0', 10);
            const expiresAt  = override > 0 ? override : loginTime + SESSION_MS;

            // Case A: localStorage already cleared before the tab was closed.
            // The modal may have been open when frozen — fire again to ensure it shows.
            if (!isLoggedIn) {
                fireSessionEndedUI();
                return;
            }

            // Case B: User object still in storage but session has now expired.
            // JavaScript was frozen so logout() was never called — do it now.
            if (loginTime && Date.now() > expiresAt) {
                userService.logout({ sessionEndedHold: true });
                fireSessionEndedUI();
            }

            // Case C: Session still valid — do nothing; normal 30s check continues.
        }

        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [SESSION_MS]);
}
